import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { ADMIN_NAMES } from '../../config/teamConfig';
import { API_URL } from '../../constants/appConstants';
import './ReportsPage.css';

interface ReportEmployee {
  id: string;
  name: string;
  type: string | null;
  label: string;
}

interface ReportMetric {
  key: string;
  label: string;
  value: string;
  valueNumber: number | null;
  note: string;
}

interface NamedValue {
  label: string;
  value: string;
  hours?: number | null;
  percent?: number | null;
}

interface MonthlyHoursRow {
  label: string;
  values: string;
  loggedHours: number | null;
  taskHours: number | null;
}

interface MonthlyTrackingRow {
  month: string;
  loginDays: number | null;
  avgLogin: string | null;
  loginEarly: number | null;
  loginLate: number | null;
  avgLogout: string | null;
  logoutEarly: number | null;
  logoutLate: number | null;
  avgDay: number | null;
  loggedHours: number | null;
  taskHours: number | null;
  utilization: string | null;
  utilizationNumber: number | null;
  tasks: number | null;
  done: number | null;
  open: number | null;
  accounts: number | null;
  review: number | null;
}

interface RecentTask {
  updatedAt: string;
  account: string;
  type: string;
  status: string;
  due: string | null;
  time: string;
  hours: number | null;
  description: string;
}

interface EmployeeStats {
  employee: ReportEmployee;
  range: {
    startMonth: number | null;
    startYear: number | null;
    endMonth: number | null;
    endYear: number | null;
  };
  employees: ReportEmployee[];
  metrics: ReportMetric[];
  monthlyHours: MonthlyHoursRow[];
  utilizationByMonth: NamedValue[];
  taskTypeMix: NamedValue[];
  topAccounts: NamedValue[];
  monthlyTracking: MonthlyTrackingRow[];
  recentTasks: RecentTask[];
}

interface TeamSummaryRow {
  employee: ReportEmployee;
  error?: string;
  taskHours: number | null;
  taskHoursLabel: string;
  loggedHours: number | null;
  loggedHoursLabel: string;
  utilization: number | null;
  utilizationLabel: string;
  completionRate: number | null;
  completionRateLabel: string;
  openTasks: number | null;
  openTasksLabel: string;
  taskTypeMix: NamedValue[];
  topAccounts: NamedValue[];
}

interface TeamStats {
  range: {
    startMonth: number | null;
    startYear: number | null;
    endMonth: number | null;
    endYear: number | null;
  };
  employees: ReportEmployee[];
  summaries: TeamSummaryRow[];
}

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - i);

const HIGHLIGHT_METRIC_KEYS = new Set([
  'task_hours',
  'logged_workday_hours',
  'task_utilization',
  'completion_rate',
  'current_open_tasks',
  'account_association',
]);

const ALL_EMPLOYEES = '';

const formatRangeLabel = (range: {
  startMonth: number | null;
  startYear: number | null;
  endMonth: number | null;
  endYear: number | null;
}) => {
  const start = MONTHS.find((m) => m.value === range.startMonth)?.label || '—';
  const end = MONTHS.find((m) => m.value === range.endMonth)?.label || '—';
  return `${start} ${range.startYear ?? ''} – ${end} ${range.endYear ?? ''}`.trim();
};

const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.username
    ? ADMIN_NAMES.some((name) => name.toLowerCase() === user.username.toLowerCase())
    : false;

  const now = new Date();
  const [employees, setEmployees] = useState<ReportEmployee[]>([]);
  const [employeeId, setEmployeeId] = useState(ALL_EMPLOYEES);
  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear] = useState(now.getFullYear());
  const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
  const [endYear, setEndYear] = useState(now.getFullYear());
  const [stats, setStats] = useState<EmployeeStats | null>(null);
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isTeamView = employeeId === ALL_EMPLOYEES;

  const loadEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/intranet/reports/employees`, {
        withCredentials: true,
      });
      const list: ReportEmployee[] = data.employees || [];
      setEmployees(list);
      setEmployeeId(ALL_EMPLOYEES);
      if (data.range?.startMonth) setStartMonth(data.range.startMonth);
      if (data.range?.startYear) setStartYear(data.range.startYear);
      if (data.range?.endMonth) setEndMonth(data.range.endMonth);
      if (data.range?.endYear) setEndYear(data.range.endYear);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load report employees';
      setError(message);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    setError(null);
    try {
      if (employeeId === ALL_EMPLOYEES) {
        const { data } = await axios.get(`${API_URL}/intranet/reports/stats`, {
          withCredentials: true,
          params: { startMonth, startYear, endMonth, endYear },
        });
        setTeamStats(data);
        setStats(null);
        if (Array.isArray(data.employees) && data.employees.length) {
          setEmployees(data.employees);
        }
      } else {
        const { data } = await axios.get(
          `${API_URL}/intranet/reports/stats/${employeeId}`,
          {
            withCredentials: true,
            params: { startMonth, startYear, endMonth, endYear },
          }
        );
        setStats(data);
        setTeamStats(null);
        if (Array.isArray(data.employees) && data.employees.length) {
          setEmployees(data.employees);
        }
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load employee stats';
      setError(message);
      setStats(null);
      setTeamStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [employeeId, startMonth, startYear, endMonth, endYear]);

  useEffect(() => {
    if (!isAdmin) return;
    loadEmployees();
  }, [isAdmin, loadEmployees]);

  useEffect(() => {
    if (!isAdmin || loadingEmployees) return;
    loadStats();
  }, [isAdmin, loadingEmployees, loadStats]);

  const highlightMetrics = useMemo(() => {
    if (!stats?.metrics) return [];
    const preferred = stats.metrics.filter((m) => HIGHLIGHT_METRIC_KEYS.has(m.key));
    return preferred.length ? preferred : stats.metrics.slice(0, 6);
  }, [stats]);

  const maxAccountHours = useMemo(() => {
    const hours = (stats?.topAccounts || []).map((a) => a.hours || 0);
    return Math.max(...hours, 1);
  }, [stats]);

  const teamTotals = useMemo(() => {
    const rows = teamStats?.summaries || [];
    const taskHours = rows.reduce((sum, row) => sum + (row.taskHours || 0), 0);
    const loggedHours = rows.reduce((sum, row) => sum + (row.loggedHours || 0), 0);
    const openTasks = rows.reduce((sum, row) => sum + (row.openTasks || 0), 0);
    return {
      employeeCount: rows.length,
      taskHours,
      loggedHours,
      openTasks,
      utilization: loggedHours > 0 ? Math.round((taskHours / loggedHours) * 100) : null,
    };
  }, [teamStats]);

  const activeRange = isTeamView ? teamStats?.range : stats?.range;

  if (!isAdmin) {
    return (
      <div className="reports-page">
        <div className="reports-empty">
          <h1>Employee Stats</h1>
          <p>You do not have access to this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      <header className="reports-header">
        <div className="reports-title-section">
          <span className="reports-eyebrow">Manager tools</span>
          <h1>Employee Stats</h1>
          <p>Intranet performance statistics by employee and date range.</p>
        </div>
        <button
          type="button"
          className="reports-refresh-btn"
          onClick={loadStats}
          disabled={loadingStats || loadingEmployees}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          {loadingStats ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      <section className="reports-filters">
        <label className="reports-field reports-field-employee">
          <span>Employee</span>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            disabled={loadingEmployees || (employees.length === 0 && !isTeamView)}
          >
            <option value={ALL_EMPLOYEES}>All employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.label || emp.name}
              </option>
            ))}
          </select>
        </label>
        <label className="reports-field">
          <span>Start</span>
          <div className="reports-date-pair">
            <select value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select value={startYear} onChange={(e) => setStartYear(Number(e.target.value))}>
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </label>
        <label className="reports-field">
          <span>End</span>
          <div className="reports-date-pair">
            <select value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))}>
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select value={endYear} onChange={(e) => setEndYear(Number(e.target.value))}>
              {YEARS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </label>
      </section>

      {error && (
        <div className="reports-error">
          <strong>Couldn’t load stats.</strong> {error}
        </div>
      )}

      {(loadingEmployees || loadingStats) && !stats && !teamStats && (
        <div className="reports-loading">
          <div className="spinner" />
          <span>
            {isTeamView
              ? 'Loading stats for all employees… this can take a minute.'
              : 'Loading intranet stats…'}
          </span>
        </div>
      )}

      {loadingStats && (stats || teamStats) && (
        <div className="reports-loading-inline">Refreshing…</div>
      )}

      {isTeamView && teamStats && (
        <>
          <section className="reports-section">
            <div className="reports-section-header">
              <h2>All Employees — Overview</h2>
              <span className="reports-range-label">
                {formatRangeLabel(teamStats.range)}
              </span>
            </div>
            <div className="reports-metric-grid">
              <div className="reports-metric-card">
                <div className="reports-metric-label">Employees</div>
                <div className="reports-metric-value">{teamTotals.employeeCount}</div>
                <div className="reports-metric-note">Active in employee stats</div>
              </div>
              <div className="reports-metric-card">
                <div className="reports-metric-label">Task Hours</div>
                <div className="reports-metric-value">
                  {teamTotals.taskHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                </div>
                <div className="reports-metric-note">Sum across all employees</div>
              </div>
              <div className="reports-metric-card">
                <div className="reports-metric-label">Logged Hours</div>
                <div className="reports-metric-value">
                  {teamTotals.loggedHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                </div>
                <div className="reports-metric-note">Sum across all employees</div>
              </div>
              <div className="reports-metric-card">
                <div className="reports-metric-label">Team Utilization</div>
                <div className="reports-metric-value">
                  {teamTotals.utilization != null ? `${teamTotals.utilization}%` : '—'}
                </div>
                <div className="reports-metric-note">Task hours / logged hours</div>
              </div>
              <div className="reports-metric-card">
                <div className="reports-metric-label">Open Tasks</div>
                <div className="reports-metric-value">{teamTotals.openTasks}</div>
                <div className="reports-metric-note">Current open across team</div>
              </div>
            </div>
          </section>

          <section className="reports-section">
            <div className="reports-section-header">
              <h2>Employee Breakdown</h2>
            </div>
            <div className="reports-table-wrap">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Task Hrs</th>
                    <th>Logged Hrs</th>
                    <th>Util.</th>
                    <th>Completion</th>
                    <th>Open</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {teamStats.summaries.map((row) => (
                    <tr key={row.employee.id}>
                      <td>{row.employee.name}</td>
                      <td>{row.employee.type || '—'}</td>
                      <td>{row.error ? '—' : row.taskHoursLabel}</td>
                      <td>{row.error ? '—' : row.loggedHoursLabel}</td>
                      <td>{row.error ? '—' : row.utilizationLabel}</td>
                      <td>{row.error ? '—' : row.completionRateLabel}</td>
                      <td>{row.error ? '—' : row.openTasksLabel}</td>
                      <td>
                        <button
                          type="button"
                          className="reports-row-link"
                          onClick={() => setEmployeeId(row.employee.id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!teamStats.summaries.length && (
                    <tr>
                      <td colSpan={8} className="reports-empty-row">No employees found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {!isTeamView && stats && (
        <>
          <section className="reports-section">
            <div className="reports-section-header">
              <h2>{stats.employee?.name || 'Employee'} — Key Metrics</h2>
              <span className="reports-range-label">
                {formatRangeLabel(activeRange || stats.range)}
              </span>
            </div>
            <div className="reports-metric-grid">
              {highlightMetrics.map((metric) => (
                <div key={metric.key} className="reports-metric-card">
                  <div className="reports-metric-label">{metric.label}</div>
                  <div className="reports-metric-value">{metric.value}</div>
                  {metric.note && <div className="reports-metric-note">{metric.note}</div>}
                </div>
              ))}
            </div>
          </section>

          <section className="reports-split">
            <div className="reports-panel">
              <h3>Task Type Mix</h3>
              <ul className="reports-list">
                {stats.taskTypeMix.map((row) => (
                  <li key={row.label}>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </li>
                ))}
                {!stats.taskTypeMix.length && <li className="reports-empty-row">No data</li>}
              </ul>
            </div>
            <div className="reports-panel">
              <h3>Top Accounts</h3>
              <ul className="reports-bar-list">
                {stats.topAccounts.map((row) => (
                  <li key={row.label}>
                    <div className="reports-bar-meta">
                      <span title={row.label}>{row.label}</span>
                      <strong>{row.value}</strong>
                    </div>
                    <div className="reports-bar-track">
                      <div
                        className="reports-bar-fill"
                        style={{ width: `${Math.round(((row.hours || 0) / maxAccountHours) * 100)}%` }}
                      />
                    </div>
                  </li>
                ))}
                {!stats.topAccounts.length && <li className="reports-empty-row">No data</li>}
              </ul>
            </div>
          </section>

          <section className="reports-section">
            <div className="reports-section-header">
              <h2>Monthly Tracking</h2>
            </div>
            <div className="reports-table-wrap">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Login Days</th>
                    <th>Avg Login</th>
                    <th>Avg Logout</th>
                    <th>Logged Hrs</th>
                    <th>Task Hrs</th>
                    <th>Util.</th>
                    <th>Tasks</th>
                    <th>Done</th>
                    <th>Open</th>
                    <th>Accounts</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.monthlyTracking.map((row) => (
                    <tr key={row.month}>
                      <td>{row.month}</td>
                      <td>{row.loginDays ?? '—'}</td>
                      <td>{row.avgLogin || '—'}</td>
                      <td>{row.avgLogout || '—'}</td>
                      <td>{row.loggedHours ?? '—'}</td>
                      <td>{row.taskHours ?? '—'}</td>
                      <td>{row.utilization || '—'}</td>
                      <td>{row.tasks ?? '—'}</td>
                      <td>{row.done ?? '—'}</td>
                      <td>{row.open ?? '—'}</td>
                      <td>{row.accounts ?? '—'}</td>
                    </tr>
                  ))}
                  {!stats.monthlyTracking.length && (
                    <tr>
                      <td colSpan={11} className="reports-empty-row">No monthly data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="reports-section">
            <div className="reports-section-header">
              <h2>Recent Task Activity</h2>
            </div>
            <div className="reports-table-wrap">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Updated</th>
                    <th>Account</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Time</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentTasks.map((task, index) => (
                    <tr key={`${task.updatedAt}-${index}`}>
                      <td>{task.updatedAt}</td>
                      <td>{task.account}</td>
                      <td>{task.type}</td>
                      <td>
                        <span className={`reports-status reports-status-${task.status.toLowerCase()}`}>
                          {task.status}
                        </span>
                      </td>
                      <td>{task.time}</td>
                      <td className="reports-desc" title={task.description}>{task.description}</td>
                    </tr>
                  ))}
                  {!stats.recentTasks.length && (
                    <tr>
                      <td colSpan={6} className="reports-empty-row">No recent tasks</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ReportsPage;
